import type { SdkPageContext } from '@ekhodealer/ekho-common';

import { extractPageContent } from './extractor';

/** Debounce ms — short enough for navigations, long enough to coalesce re-render bursts. */
const EXTRACTION_DEBOUNCE_MS = 250;

/**
 * Subscribe to every signal that can change the host page's identity in a SPA
 * without a full reload, and invoke `onUpdate` with a fresh `SdkPageContext`.
 *
 * Signals: popstate, hashchange, history.pushState/replaceState, `<title>` mutations.
 * Extractions are debounced. Fires once synchronously on start.
 *
 * @returns Cleanup function that removes all listeners and restores history patches.
 */
export function startPageWatcher(onUpdate: (ctx: SdkPageContext) => void): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  let debounceHandle: ReturnType<typeof setTimeout> | null = null;

  const fireNow = (): void => {
    try {
      onUpdate(extractPageContent());
    } catch {
      // Consumer error must not disable the watcher.
    }
  };

  const fire = (): void => {
    if (debounceHandle !== null) clearTimeout(debounceHandle);
    debounceHandle = setTimeout(() => {
      debounceHandle = null;
      fireNow();
    }, EXTRACTION_DEBOUNCE_MS);
  };

  // Initial sync extraction.
  fireNow();

  const handler: EventListener = () => fire();
  window.addEventListener('popstate', handler);
  window.addEventListener('hashchange', handler);

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  const patchedPushState: typeof window.history.pushState = function (this: History, ...args) {
    const result = originalPushState.apply(this, args);
    fire();
    return result;
  };
  const patchedReplaceState: typeof window.history.replaceState = function (
    this: History,
    ...args
  ) {
    const result = originalReplaceState.apply(this, args);
    fire();
    return result;
  };

  window.history.pushState = patchedPushState;
  window.history.replaceState = patchedReplaceState;

  let titleObserver: MutationObserver | null = null;
  const titleEl = document.querySelector('title');
  if (titleEl && typeof MutationObserver !== 'undefined') {
    titleObserver = new MutationObserver(() => fire());
    titleObserver.observe(titleEl, { childList: true, characterData: true, subtree: true });
  }

  return () => {
    if (debounceHandle !== null) {
      clearTimeout(debounceHandle);
      debounceHandle = null;
    }
    window.removeEventListener('popstate', handler);
    window.removeEventListener('hashchange', handler);
    if (window.history.pushState === patchedPushState) {
      window.history.pushState = originalPushState;
    }
    if (window.history.replaceState === patchedReplaceState) {
      window.history.replaceState = originalReplaceState;
    }
    titleObserver?.disconnect();
  };
}
